/**
 * Email Integration Service
 * SendGrid / AWS SES abstraction for transactional emails
 * Handles notifications, care statements, portal invites
 */

import LocalStorageService from '../storage/LocalStorageService';

class EmailService {
  constructor() {
    this.storageKey = 'email_messages';
    this.templateKey = 'email_templates';
    this.provider = 'sendgrid'; // 'sendgrid' | 'aws_ses'
    this.initializeTemplates();
  }

  initializeTemplates() {
    const existing = LocalStorageService.get(this.templateKey, []);
    if (existing.length > 0) return;

    const templates = [
      {
        id: 'tpl_care_statement',
        name: 'Monthly Care Statement',
        subject: 'Monthly Care Statement for {{residentName}} — {{month}} {{year}}',
        category: 'billing',
        htmlBody: '<h2>Care Statement</h2><p>Dear {{recipientName}},</p><p>Please find attached the monthly care statement for {{residentName}} at {{facilityName}}.</p>',
        variables: ['recipientName', 'residentName', 'facilityName', 'month', 'year']
      },
      {
        id: 'tpl_portal_invite',
        name: 'Family Portal Invitation',
        subject: 'You\'re invited to {{facilityName}} Family Portal',
        category: 'onboarding',
        htmlBody: '<h2>Welcome to the Family Portal</h2><p>Dear {{recipientName}},</p><p>You have been invited to access the family portal for {{residentName}}.</p><a href="{{portalLink}}">Accept Invitation</a>',
        variables: ['recipientName', 'residentName', 'facilityName', 'portalLink']
      },
      {
        id: 'tpl_incident_notification',
        name: 'Incident Notification',
        subject: 'Incident Report — {{residentName}} at {{facilityName}}',
        category: 'notification',
        htmlBody: '<h2>Incident Notification</h2><p>Dear {{recipientName}},</p><p>An incident involving {{residentName}} has been reported. Details: {{incidentSummary}}</p>',
        variables: ['recipientName', 'residentName', 'facilityName', 'incidentSummary']
      },
      {
        id: 'tpl_medication_change',
        name: 'Medication Change Notification',
        subject: 'Medication Update for {{residentName}}',
        category: 'clinical',
        htmlBody: '<h2>Medication Update</h2><p>Dear {{recipientName}},</p><p>A medication change has been made for {{residentName}}. {{changeDetails}}</p>',
        variables: ['recipientName', 'residentName', 'changeDetails']
      },
      {
        id: 'tpl_credential_expiry',
        name: 'Staff Credential Expiry Warning',
        subject: 'Action Required: Credential Expiring — {{credentialType}}',
        category: 'hr',
        htmlBody: '<h2>Credential Expiry Warning</h2><p>Dear {{staffName}},</p><p>Your {{credentialType}} expires on {{expiryDate}}. Please renew immediately to maintain rostering eligibility.</p>',
        variables: ['staffName', 'credentialType', 'expiryDate']
      },
      {
        id: 'tpl_admission_welcome',
        name: 'New Admission Welcome',
        subject: 'Welcome to {{facilityName}} — {{residentName}}',
        category: 'onboarding',
        htmlBody: '<h2>Welcome</h2><p>Dear {{recipientName}},</p><p>We warmly welcome {{residentName}} to {{facilityName}}. Your care coordinator is {{coordinatorName}}.</p>',
        variables: ['recipientName', 'residentName', 'facilityName', 'coordinatorName']
      }
    ];

    templates.forEach(t => LocalStorageService.addItem(this.templateKey, t));
  }

  async send(emailData) {
    try {
      const message = {
        id: `email-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        to: emailData.to,
        cc: emailData.cc || [],
        bcc: emailData.bcc || [],
        from: emailData.from || 'noreply@medfastcrm.com.au',
        replyTo: emailData.replyTo || null,
        subject: emailData.subject,
        htmlBody: emailData.htmlBody || '',
        textBody: emailData.textBody || '',
        templateId: emailData.templateId || null,
        templateVariables: emailData.templateVariables || {},
        category: emailData.category || 'general',
        priority: emailData.priority || 'normal',
        attachments: (emailData.attachments || []).map(a => ({ name: a.name, size: a.size, type: a.type })),
        facilityId: emailData.facilityId || null,
        residentId: emailData.residentId || null,
        staffId: emailData.staffId || null,
        sentBy: emailData.sentBy || null,
        status: 'sent',
        provider: this.provider,
        messageId: `msg_${Date.now()}`,
        deliveredAt: new Date().toISOString(),
        openedAt: null,
        clickedAt: null,
        bouncedAt: null,
        sentAt: new Date().toISOString()
      };

      if (message.templateId) {
        const template = await this.getTemplate(message.templateId);
        if (template.success) {
          message.subject = this._interpolate(template.data.subject, message.templateVariables);
          message.htmlBody = this._interpolate(template.data.htmlBody, message.templateVariables);
        }
      }

      const created = LocalStorageService.addItem(this.storageKey, message);
      console.log(`[EmailService] Sent email to ${Array.isArray(message.to) ? message.to.join(', ') : message.to}: ${message.subject}`);
      return { success: true, data: created };
    } catch (error) {
      console.error('[EmailService] Send failed:', error);
      return { success: false, error: error.message };
    }
  }

  async sendBulk(recipientList, templateId, baseVariables = {}) {
    try {
      const results = [];
      for (const recipient of recipientList) {
        const variables = { ...baseVariables, ...recipient.variables, recipientName: recipient.name };
        const result = await this.send({
          to: recipient.email,
          templateId,
          templateVariables: variables,
          category: 'bulk',
          facilityId: baseVariables.facilityId
        });
        results.push({ email: recipient.email, ...result });
      }

      const sent = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      return { success: true, data: { sent, failed, total: recipientList.length, results } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getHistory(filters = {}) {
    try {
      let messages = LocalStorageService.get(this.storageKey, []);

      if (filters.facilityId) messages = messages.filter(m => m.facilityId === filters.facilityId);
      if (filters.residentId) messages = messages.filter(m => m.residentId === filters.residentId);
      if (filters.category) messages = messages.filter(m => m.category === filters.category);
      if (filters.status) messages = messages.filter(m => m.status === filters.status);
      if (filters.startDate) messages = messages.filter(m => m.sentAt >= filters.startDate);
      if (filters.endDate) messages = messages.filter(m => m.sentAt <= filters.endDate);

      messages.sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));
      return { success: true, data: messages };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getTemplate(templateId) {
    try {
      const templates = LocalStorageService.get(this.templateKey, []);
      const template = templates.find(t => t.id === templateId);
      return template ? { success: true, data: template } : { success: false, error: 'Template not found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getTemplates() {
    try {
      const templates = LocalStorageService.get(this.templateKey, []);
      return { success: true, data: templates };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getStats(facilityId = null, period = 30) {
    try {
      let messages = LocalStorageService.get(this.storageKey, []);
      if (facilityId) messages = messages.filter(m => m.facilityId === facilityId);

      const cutoff = new Date(Date.now() - period * 24 * 60 * 60 * 1000).toISOString();
      messages = messages.filter(m => m.sentAt >= cutoff);

      return {
        success: true,
        data: {
          totalSent: messages.length,
          delivered: messages.filter(m => m.status === 'sent' || m.status === 'delivered').length,
          opened: messages.filter(m => m.openedAt).length,
          bounced: messages.filter(m => m.bouncedAt).length,
          byCategory: messages.reduce((acc, m) => { acc[m.category] = (acc[m.category] || 0) + 1; return acc; }, {}),
          openRate: messages.length > 0 ? ((messages.filter(m => m.openedAt).length / messages.length) * 100).toFixed(1) : 0
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  _interpolate(template, variables) {
    return template.replace(/\{\{(\w+)\}\}/g, (_, key) => variables[key] || `{{${key}}}`);
  }
}

export default new EmailService();
